import { useQuery } from "react-query";
import { authHeaders } from "../authentication/auth-header";
import { getApiErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useSessionContext } from "../authentication/session.context";
import {
  getDashboardController,
  getTotalExpectedBookingRevenuePatientsController,
  getTotalPaidPatientsController,
  getTotalPendingPatientsController,
  getPatientSummaryController,
  getUpcomingSessionsController,
  GetDashboardModel,
  GetDashboardPatientModel,
  GetDashboardPatientSummaryModel,
  GetResponseDashboardModel,
  GetResponseDashboardUpcomingSessionsModel,
  QueryKeyDashboard,
  QueryKeyDashboardExpectedRevenuePatients,
  QueryKeyDashboardPaidPatients,
  QueryKeyDashboardPendingPatients,
  QueryKeyDashboardPatientSummary,
  QueryKeyDashboardUpcomingSessions,
} from "@factory/model/query-model-dashboard";

// Reuse dashboard data when navigating back within this window instead of
// refetching on every visit. Any successful write elsewhere in the app marks
// it stale immediately (MutationCache in main-app.tsx), and the login page
// clears the whole cache, so this only bounds how long changes made by
// *other* users can take to show up.
const DASHBOARD_STALE_TIME = 60 * 1000;

export function useQyGetDashboard(
  search: string,
  filter?: Record<string, string>,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDashboardModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getDashboardController(
      search,
      filter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetDashboardModel[];
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    queryKey: [
      QueryKeyDashboard,
      search,
      filter,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(search, filter, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetTotalExpectedBookingRevenuePatients(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDashboardPatientModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getTotalExpectedBookingRevenuePatientsController(
      undefined,
      undefined,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    // API returns array of patients
    return (response["data"] as unknown) as GetDashboardPatientModel[];
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    queryKey: [
      QueryKeyDashboardExpectedRevenuePatients,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetTotalPaidPatients(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDashboardPatientModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getTotalPaidPatientsController(
      undefined,
      undefined,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    // API returns array of patients
    return (response["data"] as unknown) as GetDashboardPatientModel[];
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    queryKey: [QueryKeyDashboardPaidPatients, dateFilter, startDate, endDate],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetTotalPendingPatients(
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDashboardPatientModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getTotalPendingPatientsController(
      undefined,
      undefined,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    // API returns array of patients
    return (response["data"] as unknown) as GetDashboardPatientModel[];
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    queryKey: [
      QueryKeyDashboardPendingPatients,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetPatientSummary(
  patientId: string,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDashboardPatientSummaryModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    patientId: string,
    dateFilter: string | undefined = undefined,
    startDate: string | undefined = undefined,
    endDate: string | undefined = undefined
  ) => {
    await checkSession();

    showProgress();
    const operation = await getPatientSummaryController(
      patientId,
      undefined,
      undefined,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response["data"] as GetDashboardPatientSummaryModel;
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    queryKey: [
      QueryKeyDashboardPatientSummary,
      patientId,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: () => apiFn(patientId, dateFilter, startDate, endDate),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetUpcomingSessions(
  search: string,
  limit: number,
  offset: number,
  order?: Record<string, string>,
  filter?: Record<string, string>,
  dateFilter?: string,
  startDate?: string,
  endDate?: string,
  enabled?: boolean,
  onSuccess?:
    | ((
        data: GetResponseDashboardUpcomingSessionsModel
      ) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async () => {
    await checkSession();

    showProgress();
    const operation = await getUpcomingSessionsController(
      search || undefined,
      limit,
      offset,
      order && Object.keys(order).length > 0
        ? { order: JSON.stringify(order) }
        : undefined,
      filter,
      dateFilter,
      startDate,
      endDate,
      authHeaders()
    );
    const response = (await operation()).data;
    return response[
      "data"
    ] as unknown as GetResponseDashboardUpcomingSessionsModel;
  };

  return useQuery({
    enabled,
    staleTime: DASHBOARD_STALE_TIME,
    // Keep the current page on screen while the next one loads
    keepPreviousData: true,
    queryKey: [
      QueryKeyDashboardUpcomingSessions,
      search,
      limit,
      offset,
      order,
      filter,
      dateFilter,
      startDate,
      endDate,
    ],
    queryFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
